import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Flame, ShieldAlert, ShieldCheck, Clock, Plus, Trash2, CheckCircle2, Filter, CalendarClock, ChevronRight, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PhotoAttach from "../components/PhotoAttach";

const TYPES = [
    { key: "pest_control", label: "Pest control" },
    { key: "spray_suction", label: "Spray suction cleaning" },
    { key: "fire_extinguisher", label: "Fire extinguisher refill", fire: true },
    { key: "ac_amc", label: "AC AMC" },
    { key: "ups_amc", label: "UPS AMC" },
    { key: "other_amc", label: "Other AMC" },
    { key: "licence_renewal", label: "Licence renewal" },
    { key: "other", label: "Other" },
];
const FREQ = [
    { key: "monthly", label: "Monthly" },
    { key: "quarterly", label: "Quarterly" },
    { key: "half_yearly", label: "Half-yearly" },
    { key: "yearly", label: "Yearly" },
    { key: "custom", label: "Custom (N days)" },
];
const STATUS_CHIP = {
    OVERDUE: "bg-red-100 text-[#E02B2B] border-red-200",
    DUE_SOON: "bg-amber-50 text-amber-700 border-amber-200",
    OK: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const STATUS_LABEL = { OVERDUE: "OVERDUE", DUE_SOON: "DUE SOON", OK: "OK" };

function typeLabel(t) { return TYPES.find((x) => x.key === t)?.label || t; }

export default function CompliancePage() {
    const { user } = useAuth();
    const isAdmin = user?.role === "HO_ADMIN";
    const [sites, setSites] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [siteFilter, setSiteFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [creating, setCreating] = useState(false);
    const [logging, setLogging] = useState(null); // item obj
    const [detail, setDetail] = useState(null); // item obj

    const load = useCallback(async () => {
        try {
            const [items, s] = await Promise.all([
                api.get("/compliance"),
                sites.length === 0 ? api.get("/sites") : Promise.resolve({ data: sites }),
            ]);
            setRows(items.data);
            if (sites.length === 0) setSites(s.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [sites.length]);
    useEffect(() => { load(); }, [load]);

    // Group by status; fire pinned to top of each group. Apply filters client-side too so the
    // dropdowns feel snappy and can't be defeated by any API-side visibility rule (belt & braces).
    const filteredRows = useMemo(() => {
        return rows.filter((r) => {
            if (siteFilter && r.site_id !== siteFilter) return false;
            if (typeFilter && r.type !== typeFilter) return false;
            if (statusFilter && r.status !== statusFilter) return false;
            return true;
        });
    }, [rows, siteFilter, typeFilter, statusFilter]);

    const grouped = useMemo(() => {
        const order = ["OVERDUE", "DUE_SOON", "OK"];
        const byStatus = { OVERDUE: [], DUE_SOON: [], OK: [] };
        for (const r of filteredRows) byStatus[r.status].push(r);
        for (const k of order) {
            byStatus[k].sort((a, b) => {
                if (a.is_fire !== b.is_fire) return a.is_fire ? -1 : 1;
                return (a.days_to_due || 0) - (b.days_to_due || 0);
            });
        }
        return order.map((k) => ({ status: k, items: byStatus[k] }));
    }, [filteredRows]);

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Compliance</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">AMC & Compliance Calendar</h1>
                    <p className="text-sm text-gray-500 mt-1">Recurring obligations across your sites. Fire-safety items are pinned to the top of each group.</p>
                </div>
                {isAdmin && (
                    <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="compliance-add-btn"><Plus className="w-4 h-4" /> Add item</button>
                )}
            </div>

            {/* Filter bar */}
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
                <div className="inline-flex items-center gap-1 text-gray-500"><Filter className="w-3.5 h-3.5" /> Filters</div>
                <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} className="rounded border border-gray-300 bg-white px-2 py-1.5" data-testid="compliance-filter-site">
                    <option value="">All sites</option>
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded border border-gray-300 bg-white px-2 py-1.5" data-testid="compliance-filter-type">
                    <option value="">All types</option>
                    {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded border border-gray-300 bg-white px-2 py-1.5" data-testid="compliance-filter-status">
                    <option value="">All statuses</option>
                    <option value="OVERDUE">Overdue</option>
                    <option value="DUE_SOON">Due soon</option>
                    <option value="OK">OK</option>
                </select>
                <span className="text-gray-400">·</span>
                <span className="text-gray-500" data-testid="compliance-filtered-count">{filteredRows.length} of {rows.length} item{rows.length !== 1 ? "s" : ""}</span>
            </div>

            {loading ? (
                <div className="mt-6 h-40 rounded-xl bg-white border border-gray-100 animate-pulse" />
            ) : (
                <div className="mt-6 space-y-6">
                    {grouped.map(({ status, items }) => items.length > 0 && (
                        <div key={status} data-testid={`group-${status}`}>
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold border ${STATUS_CHIP[status]}`}>{STATUS_LABEL[status]}</span>
                                <span className="text-xs text-gray-500">{items.length}</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {items.map((r) => <ItemCard key={r.id} row={r} onLog={() => setLogging(r)} onOpen={() => setDetail(r)} isAdmin={isAdmin} />)}
                            </div>
                        </div>
                    ))}
                    {rows.length === 0 || filteredRows.length === 0 ? (
                        <div className="rounded-xl bg-white border border-gray-100 p-10 text-center text-sm text-gray-500" data-testid="compliance-empty">
                            {rows.length === 0 ? "No compliance items yet." : "No compliance items match those filters."}
                        </div>
                    ) : null}
                </div>
            )}

            {creating && <CreateEditModal sites={sites} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
            {logging && <LogVisitModal item={logging} onClose={() => setLogging(null)} onSaved={() => { setLogging(null); load(); }} />}
            {detail && <DetailPanel item={detail} isAdmin={isAdmin} onClose={() => setDetail(null)} onChanged={() => load()} onLog={(it) => { setDetail(null); setLogging(it); }} />}
        </div>
    );
}

function ItemCard({ row, onLog, onOpen, isAdmin }) {
    const fire = row.is_fire;
    return (
        <div
            className={`rounded-xl bg-white border-2 fx-shadow-card overflow-hidden ${fire ? "border-[#E02B2B]" : "border-gray-100"}`}
            data-testid={`compliance-item-${row.id}`}
        >
            <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5 min-w-0">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${fire ? "bg-red-100 text-[#E02B2B]" : row.status === "OVERDUE" ? "bg-red-100 text-[#E02B2B]" : row.status === "DUE_SOON" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                            {fire ? <Flame className="w-4 h-4" /> : row.status === "OVERDUE" ? <ShieldAlert className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{row.site_name}</span>
                                {fire && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-white bg-[#E02B2B] px-1.5 py-0.5 rounded" data-testid={`fire-badge-${row.id}`}><Flame className="w-2.5 h-2.5" /> Fire safety</span>}
                            </div>
                            <button onClick={onOpen} className="text-left font-display font-semibold text-[#1E3355] leading-tight mt-0.5 hover:underline truncate w-full" data-testid={`compliance-open-${row.id}`}>{row.name}</button>
                            <div className="text-[11px] text-gray-500 mt-0.5">{typeLabel(row.type)} · {row.frequency.replace("_", "-")}{row.vendor ? ` · ${row.vendor}` : ""}</div>
                        </div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold border ${STATUS_CHIP[row.status]}`} data-testid={`compliance-status-${row.id}`}>{STATUS_LABEL[row.status]}</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                    <div className="rounded-md bg-[#F4F6F9] px-2.5 py-1.5">
                        <div className="text-gray-500 uppercase tracking-wider text-[9px]">Last done</div>
                        <div className="text-[#1E3355] font-semibold">{row.last_done_date ? fmtDate(row.last_done_date) : "—"}</div>
                    </div>
                    <div className={`rounded-md px-2.5 py-1.5 ${row.status === "OVERDUE" ? "bg-red-50" : row.status === "DUE_SOON" ? "bg-amber-50" : "bg-[#F4F6F9]"}`}>
                        <div className="text-gray-500 uppercase tracking-wider text-[9px]">Next due</div>
                        <div className={`font-semibold ${row.status === "OVERDUE" ? "text-[#E02B2B]" : row.status === "DUE_SOON" ? "text-amber-700" : "text-[#1E3355]"}`}>
                            {fmtDate(row.next_due_date)} <span className="text-gray-500 font-normal">· {row.days_to_due < 0 ? `${-row.days_to_due}d overdue` : `${row.days_to_due}d`}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 divide-x divide-gray-100 border-t border-gray-100 bg-[#FAFBFC]">
                <button onClick={onLog} className="flex items-center justify-center gap-1 py-2.5 text-xs font-semibold text-[#1E3355] hover:bg-[#F4F6F9]" data-testid={`compliance-log-${row.id}`}>
                    <CheckCircle2 className="w-4 h-4" /> Log completed visit
                </button>
                <button onClick={onOpen} className="flex items-center justify-center gap-1 py-2.5 text-xs font-medium text-gray-600 hover:bg-[#F4F6F9]" data-testid={`compliance-detail-${row.id}`}>
                    History <ChevronRight className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}

function CreateEditModal({ sites, onClose, onSaved, existing = null }) {
    const [form, setForm] = useState(existing || {
        site_id: sites[0]?.id || "", name: "", type: "pest_control", vendor: "",
        frequency: "monthly", custom_days: 30, last_done_date: "", cost_note: "", notes: "",
    });
    async function submit(e) {
        e.preventDefault();
        try {
            const body = { ...form, last_done_date: form.last_done_date || null };
            if (body.frequency !== "custom") delete body.custom_days;
            if (existing) await api.patch(`/compliance/${existing.id}`, body);
            else await api.post("/compliance", body);
            toast.success(existing ? "Updated" : "Item added");
            onSaved();
        } catch (e) { toast.error(apiError(e)); }
    }
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="compliance-create-modal">
            <form onSubmit={submit} className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-100">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">Compliance item</div>
                    <div className="font-display font-semibold text-[#1E3355] mt-0.5">{existing ? "Edit item" : "New item"}</div>
                </div>
                <div className="p-5 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Site"><select required value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className={fieldCls} data-testid="compliance-form-site">
                            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select></Field>
                        <Field label="Type"><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={fieldCls} data-testid="compliance-form-type">
                            {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select></Field>
                    </div>
                    <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} data-testid="compliance-form-name" /></Field>
                    <Field label="Vendor"><input value={form.vendor || ""} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={fieldCls} data-testid="compliance-form-vendor" /></Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Frequency"><select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className={fieldCls} data-testid="compliance-form-frequency">
                            {FREQ.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select></Field>
                        {form.frequency === "custom" ? (
                            <Field label="Custom (days)"><input type="number" value={form.custom_days || ""} onChange={(e) => setForm({ ...form, custom_days: Number(e.target.value) })} className={fieldCls} min="1" data-testid="compliance-form-custom" /></Field>
                        ) : (
                            <Field label="Last done"><input type="date" value={form.last_done_date || ""} onChange={(e) => setForm({ ...form, last_done_date: e.target.value })} className={fieldCls} data-testid="compliance-form-last-done" /></Field>
                        )}
                    </div>
                    {form.frequency === "custom" && (
                        <Field label="Last done"><input type="date" value={form.last_done_date || ""} onChange={(e) => setForm({ ...form, last_done_date: e.target.value })} className={fieldCls} data-testid="compliance-form-last-done-custom" /></Field>
                    )}
                    <Field label="Cost note"><input value={form.cost_note || ""} onChange={(e) => setForm({ ...form, cost_note: e.target.value })} className={fieldCls} data-testid="compliance-form-cost" placeholder="e.g. ₹4,500 / visit" /></Field>
                    <Field label="Notes"><textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={fieldCls} data-testid="compliance-form-notes" /></Field>
                </div>
                <div className="flex border-t border-gray-100">
                    <button type="button" onClick={onClose} className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="compliance-create-cancel">Cancel</button>
                    <button type="submit" className="flex-1 py-3 text-sm font-semibold text-white bg-[#1E3355] hover:bg-[#15243C]" data-testid="compliance-create-submit">Save</button>
                </div>
            </form>
        </div>
    );
}

function LogVisitModal({ item, onClose, onSaved }) {
    const [visitId, setVisitId] = useState(null);
    const [form, setForm] = useState({
        visit_date: new Date().toISOString().slice(0, 10),
        vendor: item.vendor || "", cost_note: "", note: "",
    });
    async function submit(e) {
        e.preventDefault();
        try {
            const { data } = await api.post(`/compliance/${item.id}/log-visit`, form);
            setVisitId(data.id);
            toast.success("Visit logged. Attach photo below then close.");
        } catch (e) { toast.error(apiError(e)); }
    }
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="compliance-log-modal">
            <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-100 flex items-start gap-3">
                    {item.is_fire && <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-100 text-[#E02B2B]"><Flame className="w-4 h-4" /></span>}
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">Log completed visit</div>
                        <div className="font-display font-semibold text-[#1E3355] mt-0.5">{item.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{item.site_name} · {typeLabel(item.type)}</div>
                    </div>
                </div>
                {!visitId ? (
                    <form onSubmit={submit} className="p-5 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="Visit date"><input type="date" required max={new Date().toISOString().slice(0,10)} value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className={fieldCls} data-testid="log-visit-date" /></Field>
                            <Field label="Vendor"><input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={fieldCls} data-testid="log-visit-vendor" /></Field>
                        </div>
                        <Field label="Cost note"><input value={form.cost_note} onChange={(e) => setForm({ ...form, cost_note: e.target.value })} placeholder="e.g. ₹4,500" className={fieldCls} data-testid="log-visit-cost" /></Field>
                        <Field label="Notes"><textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={fieldCls} data-testid="log-visit-note" /></Field>
                        <div className="text-[11px] text-gray-500">Saving this visit sets last-done to the visit date and recomputes next-due. Attach photos after saving.</div>
                        <div className="flex gap-2 pt-1">
                            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-md border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="log-visit-cancel">Cancel</button>
                            <button type="submit" className="flex-1 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold" data-testid="log-visit-submit">Save visit</button>
                        </div>
                    </form>
                ) : (
                    <div className="p-5 space-y-3">
                        <div className="text-sm text-gray-700">Visit saved. Attach evidence photo(s):</div>
                        <PhotoAttach parentType="compliance_visit" parentId={visitId} siteId={item.site_id} tag="visit" testIdPrefix="log-visit-photo" />
                        <button onClick={onSaved} className="w-full py-2.5 rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold" data-testid="log-visit-done">Done</button>
                    </div>
                )}
            </div>
        </div>
    );
}

function DetailPanel({ item, isAdmin, onClose, onChanged, onLog }) {
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/compliance/${item.id}`);
            setVisits(data.visits || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [item.id]);
    useEffect(() => { load(); }, [load]);

    async function del() {
        if (!window.confirm(`Delete "${item.name}"? Visit history will also be deleted.`)) return;
        try { await api.delete(`/compliance/${item.id}`); toast.success("Deleted"); onChanged(); onClose(); }
        catch (e) { toast.error(apiError(e)); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="compliance-detail-panel">
            <div className="w-full sm:max-w-xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
                <div className={`p-5 border-b flex items-start justify-between gap-3 ${item.is_fire ? "bg-red-50 border-red-200" : "border-gray-100"}`}>
                    <div className="flex items-start gap-3">
                        {item.is_fire && <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#E02B2B] text-white"><Flame className="w-4 h-4" /></span>}
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">{item.site_name} · {typeLabel(item.type)}</div>
                            <div className="font-display font-semibold text-[#1E3355] mt-0.5">{item.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                                {item.frequency.replace("_", "-")}{item.vendor ? ` · ${item.vendor}` : ""}{item.cost_note ? ` · ${item.cost_note}` : ""}
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="compliance-detail-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="rounded-md bg-[#F4F6F9] p-3">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Last done</div>
                            <div className="text-[#1E3355] font-semibold mt-1">{item.last_done_date ? fmtDate(item.last_done_date) : "—"}</div>
                        </div>
                        <div className={`rounded-md p-3 ${item.status === "OVERDUE" ? "bg-red-50" : item.status === "DUE_SOON" ? "bg-amber-50" : "bg-emerald-50"}`}>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Next due</div>
                            <div className={`font-semibold mt-1 ${item.status === "OVERDUE" ? "text-[#E02B2B]" : item.status === "DUE_SOON" ? "text-amber-800" : "text-emerald-700"}`}>
                                {fmtDate(item.next_due_date)} <span className="text-xs">({STATUS_LABEL[item.status]})</span>
                            </div>
                        </div>
                    </div>
                    {item.notes && <div className="text-sm text-gray-700"><span className="font-semibold">Notes: </span>{item.notes}</div>}

                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Visit history</div>
                        {loading ? <div className="text-sm text-gray-500">Loading…</div> :
                        visits.length === 0 ? <div className="text-sm text-gray-500">No visits recorded yet.</div> :
                        <ul className="space-y-2" data-testid="compliance-visits-list">
                            {visits.map((v) => (
                                <li key={v.id} className="rounded-md border border-gray-100 bg-[#FAFBFC] p-3" data-testid={`visit-${v.id}`}>
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="font-semibold text-[#1E3355] text-sm">{fmtDate(v.visit_date)}</div>
                                        {v.cost_note && <span className="text-[11px] text-gray-500">{v.cost_note}</span>}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-0.5">{v.vendor || "—"} · logged by {v.logged_by_name}</div>
                                    {v.note && <div className="text-xs text-gray-700 mt-1">{v.note}</div>}
                                    <div className="mt-2"><PhotoAttach parentType="compliance_visit" parentId={v.id} siteId={item.site_id} tag="visit" compact disabled testIdPrefix={`visit-${v.id}-photo`} /></div>
                                </li>
                            ))}
                        </ul>}
                    </div>
                </div>

                <div className="flex border-t border-gray-100">
                    <button onClick={() => onLog(item)} className="flex-1 py-3 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center justify-center gap-1" data-testid="detail-log-visit"><CheckCircle2 className="w-4 h-4" /> Log visit</button>
                    {isAdmin && (
                        <>
                            <button onClick={() => setEditing(true)} className="flex-1 py-3 text-sm font-semibold text-[#1E3355] bg-white border-l border-gray-100 hover:bg-gray-50" data-testid="detail-edit">Edit</button>
                            <button onClick={del} className="py-3 px-4 text-sm font-semibold text-[#E02B2B] bg-white border-l border-gray-100 hover:bg-red-50 inline-flex items-center gap-1" data-testid="detail-delete"><Trash2 className="w-4 h-4" /></button>
                        </>
                    )}
                </div>
            </div>
            {editing && <CreateEditModal sites={[{ id: item.site_id, name: item.site_name }]} existing={item} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged(); onClose(); }} />}
        </div>
    );
}

const fieldCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white";
function Field({ label, children }) { return (<div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}</div>); }
